import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation } from 'convex/react';
import { AlertTriangle, Clock, X } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import { Button } from '@bill-alps/ui';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useTranslation } from '../../i18n/useTranslation';
import {
  daysUntilTrialEnds,
  isTrialEndingSoon,
  isTrialExpired,
} from './trial-helpers';

/** Routes where the banner must never appear (onboarding / upsell flow). */
const EXCLUDED_PATH_PREFIXES = ['/onboarding', '/welcome'];

/**
 * Global bar shown across the authenticated app: a countdown in the final
 * days of the trial, then a dismissible notice once it has lapsed to Free.
 */
export function TrialBanner() {
  const { user, isAccountAdmin, isFiduciaire } = useCurrentUser();
  const { t } = useTranslation('trialBanner');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dismissNotice = useMutation(
    api.features.companies.records.dismissTrialNotice,
  );
  const [dismissed, setDismissed] = useState(false);

  const company = user?.company;
  const onExcludedRoute = EXCLUDED_PATH_PREFIXES.some((p) =>
    pathname.startsWith(p),
  );
  // Fiduciaires have no trial — their mandates are billed by volume.
  if (!company || !isAccountAdmin || onExcludedRoute || isFiduciaire) {
    return null;
  }

  const { plan, trialEndsAt, dismissedTrialNotice } = company;
  const goToPlans = () => navigate('/account#plan-section');

  if (isTrialEndingSoon(plan, trialEndsAt) && trialEndsAt) {
    const days = daysUntilTrialEnds(trialEndsAt);
    return (
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b-2 border-border bg-riso-amber/15 px-4 py-2 text-sm">
        <span className="flex items-center gap-2 font-medium text-foreground">
          <Clock className="h-4 w-4 shrink-0" />
          {t('endingSoon', { count: days })}
        </span>
        <Button variant="accent" size="sm" onClick={goToPlans}>
          {t('subscribeCta')}
        </Button>
      </div>
    );
  }

  if (
    isTrialExpired(plan, trialEndsAt) &&
    !dismissedTrialNotice &&
    !dismissed
  ) {
    const handleDismiss = () => {
      setDismissed(true);
      void dismissNotice({ companyId: company._id });
    };
    return (
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b-2 border-border bg-muted px-4 py-2 text-sm">
        <span className="flex items-center gap-2 font-medium text-foreground">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {t('expiredBody')}
        </span>
        <Button variant="accent" size="sm" onClick={goToPlans}>
          {t('subscribeCta')}
        </Button>
        <button
          type="button"
          aria-label={t('dismiss')}
          onClick={handleDismiss}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
